import { GlobalError } from 'react-hook-form';
import { useTranslation } from 'utils/translation';

export function useFormError(error?: GlobalError | string): string | undefined {
  const { t } = useTranslation();

  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    error.message
  ) {
    return error.message;
  }

  if (error && typeof error === 'object' && 'type' in error && error.type) {
    const type = String(error.type);

    return t(`validation.${type}`);
  }

  if (typeof error === 'string') {
    return error;
  }
}

type Violation = {
  propertyPath: string;
  message: string;
};

function toSnakeCase(str: string): string {
  return str.replace(/([A-Z])/g, '_$1').toLowerCase();
}

function transformValidationErrors(errors: any): any {
  if (!errors || typeof errors !== 'object') {
    return errors;
  }

  const transformed: any = {};

  for (const key in errors) {
    if (Object.prototype.hasOwnProperty.call(errors, key)) {
      transformed[toSnakeCase(key)] = errors[key];
    }
  }

  return transformed;
}

export function useServerErrors(error: any) {
  if (!error) {
    return undefined;
  }

  if (
    error?.response?.headers?.['content-type'] ===
      'application/problem+json; charset=utf-8' &&
    Array.isArray(error?.response?.data?.violations)
  ) {
    const validationErrors = Array.from<Violation>(
      error?.response?.data?.violations || [],
    ).map((item) => [item.propertyPath, item.message]);

    return Object.fromEntries(validationErrors);
  }

  const legacy = error?.response?.data?.errors?.children;
  if (legacy) {
    let errors = {} as any;

    Object.keys(legacy).map((key) => {
      errors[key] = Array.isArray(legacy[key]?.errors)
        ? legacy[key].errors.at(0)
        : null;
    });

    return errors;
  }

  const snakeCasedErrors = transformValidationErrors(
    error?.response?.data?.errors,
  );

  return {
    ...snakeCasedErrors,
    ...error?.response?.data?.errors,
  };
}
